import { useState } from 'react'
import { createFileRoute, useCanGoBack, useNavigate, useParams, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { useSwitchToLinkedAccount } from '@/features/auth/hooks/useSwitchToLinkedAccount'
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react'
import { useIncidentDetail } from '@/entities/incident'
import {
  IncidentDetailHeader,
  IncidentAnswers,
  IncidentAttachments,
  IncidentLocationCard,
  IncidentWeatherCard,
  IncidentStatusHistoryPanel,
  IncidentFollowUpTimeline,
  IncidentFollowUpComposer,
} from '@/features/incident-detail'
import {
  EditedNotice,
  IncidentReportActions,
  IncidentReportEditor,
  IncidentReportHistoryPanel,
} from '@/features/incident-report'
import { Button } from '@/shared/components/ui/button'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { useApiErrorMessage } from '@/shared/hooks/useApiErrorMessage'
import { usePermissions } from '@/entities/session/hooks/usePermissions'
import { PERMISSIONS } from '@/shared/config/permissions'

const searchSchema = z.object({
  account: z.coerce.number().int().positive().optional(),
})

export const Route = createFileRoute('/incidents/$incidentId')({
  component: IncidentDetailRoute,
  staticData: { authorize: true },
  validateSearch: searchSchema,
})

function IncidentDetailRoute() {
  const { account } = Route.useSearch()
  const { incidentId } = Route.useParams()
  const switchingAccount = useSwitchToLinkedAccount(account)

  if (switchingAccount) return null

  return <IncidentDetailPage key={incidentId} />
}

function IncidentDetailPage() {
  const { incidentId } = useParams({ from: '/incidents/$incidentId' })
  const id = Number(incidentId)
  const query = useIncidentDetail(id)
  const errMessage = useApiErrorMessage(query.error)
  const canGoBack = useCanGoBack()
  const router = useRouter()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [editing, setEditing] = useState(false)
  const goBack = () => (canGoBack ? router.history.back() : navigate({ to: '/incidents' }))

  const breadcrumbs = [
    { label: 'Incidents', href: '/incidents' },
    { label: query.data?.reference ?? 'Incident' },
  ]

  if (query.isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="px-4 pt-4 pb-2">
          <PageHeader breadcrumbs={breadcrumbs} />
        </div>
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading incident…</span>
        </div>
      </div>
    )
  }

  if (!query.data) {
    return (
      <div className="flex h-full flex-col">
        <div className="px-4 pt-4 pb-2">
          <PageHeader breadcrumbs={breadcrumbs} />
        </div>
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="max-w-md w-full text-center p-6 rounded-lg border bg-card">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">Couldn't load this incident</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {errMessage ?? 'The incident may have been removed, or there was a network error.'}
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={goBack}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                {canGoBack ? 'Back' : 'Back to list'}
              </Button>
              <Button size="sm" onClick={() => query.refetch()} disabled={query.isFetching}>
                {query.isFetching ? 'Retrying…' : 'Retry'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const incident = query.data
  const isResolved = incident.status === 'Resolved'
  const canReviewReport = hasPermission(PERMISSIONS.IncidentsReview)

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 pt-3">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>
      {query.isError && (
        <div
          role="alert"
          className="mx-4 mt-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">Couldn't refresh this incident.</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      )}
      <IncidentDetailHeader
        incident={incident}
        notice={<EditedNotice incident={incident} />}
        actions={
          canReviewReport ? (
            <IncidentReportActions incident={incident} editing={editing} onEdit={() => setEditing(true)} />
          ) : undefined
        }
      />
      <div className="flex-1 overflow-y-auto">
        {editing ? (
          <IncidentReportEditor incident={incident} onDone={() => setEditing(false)} />
        ) : (
          <>
            <div className="p-4 border-b">
              <h3 className="text-sm font-semibold mb-2">Description</h3>
              <p className="text-sm whitespace-pre-wrap">{incident.description}</p>
            </div>
            <IncidentAnswers incident={incident} />
          </>
        )}
        <IncidentAttachments attachments={incident.attachments} />
        <IncidentLocationCard latitude={incident.latitude} longitude={incident.longitude} />
        <IncidentWeatherCard incident={incident} />
        <IncidentStatusHistoryPanel history={incident.statusHistory} />
        {canReviewReport && <IncidentReportHistoryPanel incidentId={incident.id} />}
        <IncidentFollowUpTimeline followUps={incident.followUps} />
      </div>
      {!isResolved && <IncidentFollowUpComposer incidentId={incident.id} />}
    </div>
  )
}
